import { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { KimInput } from './kim-input';
import { useKimFileUpload } from '../hooks/use-kim-file-upload';
import { EditableTemplate, TemplateBuilderDialog } from '../template-builder/template-builder-dialog';
import { TemplatesPopover } from '../templates/templates-popover';
import { WEXLER_PROMPT_TEMPLATES } from '../templates/wexler-prompt-templates';
import { KIM_FILE_INPUT_ACCEPT } from '../utils/upload-constants';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateKimThread } from '@/api/mutations/useCreateKimThread';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { DocMentionsInputHandle, extractKimMentions } from '@/new/components/doc-mentions-input';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

export function NewKimLargeInput() {
  const { matterId, subsetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [question, setQuestion] = useState('');
  const [isTemplateBuilderOpen, setIsTemplateBuilderOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EditableTemplate | null>(null);
  const [templateInitialDraft, setTemplateInitialDraft] = useState<{ title: string; body: string } | undefined>(
    undefined,
  );
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState(false);
  const inputRef = useRef<DocMentionsInputHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const questionFromSearch = next.get('question')?.trim();
    const shouldAutoSubmit = next.get('autoSubmit') === 'true';

    if (questionFromSearch) {
      next.delete('question');
      next.delete('autoSubmit');
      setSearchParams(next, { replace: true });
      setQuestion(questionFromSearch);
      if (shouldAutoSubmit) setPendingAutoSubmit(true);
      return;
    }

    const templateId = next.get('template');
    if (templateId) {
      const template = WEXLER_PROMPT_TEMPLATES.find((t) => t.id === templateId);
      if (template) {
        setQuestion(template.template);
      }

      next.delete('template');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.has('question') || searchParams.has('template')) return;

    const questionFromState = (location.state as { question?: string } | null)?.question?.trim();
    if (questionFromState) {
      setQuestion(questionFromState);
    }
  }, []);

  const { mutate: createThread, isLoading } = useCreateKimThread(matterId, subsetId);
  const { uploadedFiles, isUploading, addFiles, removeFile, clearFiles } = useKimFileUpload(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const suggestedQuestions = useMemo(() => {
    const questions = subset?.exampleKimQuestions?.question?.map((q) => q.prompt) ?? [];
    const drafts = subset?.exampleKimQuestions?.draft?.map((q) => q.prompt) ?? [];
    return [...questions, ...drafts];
  }, [subset]);

  const handleTemplateSelect = (body: string) => {
    setQuestion(body);
    inputRef.current?.focus();
  };

  const handleSubmit = () => {
    const trimmed = question.trim();
    if (!trimmed || isUploading) return;

    const files = uploadedFiles.length > 0 ? uploadedFiles : undefined;

    createThread(
      { message: trimmed, files, mentions: extractKimMentions(trimmed) },
      {
        onSuccess: (data) => {
          clearFiles();
          navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/${data.id}`);
        },
        onError: () => {
          toast.error('Error creating thread');
        },
      },
    );
  };

  useEffect(() => {
    if (!pendingAutoSubmit) return;
    if (!question.trim() || isUploading || isLoading) return;
    setPendingAutoSubmit(false);
    handleSubmit();
  }, [pendingAutoSubmit, question, isUploading, isLoading]);

  return (
    <div className="mb-12 flex w-full max-w-xl flex-col items-center">
      <h2 className="mb-10 font-serif text-2xl font-medium">Ask Kim...</h2>

      <div className="mb-1.5 flex w-full justify-between">
        <div className="flex">
          <Button variant={'ghost'} size={'sm'} onClick={() => inputRef.current?.triggerMention()}>
            <Icon className="lucide--at-sign" /> Mention
          </Button>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size={'sm'}
                disabled={isLoading || isUploading}
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon className="lucide--paperclip" />
                Attach files
                <NewChip className="relative top-auto right-auto mb-0.5 ml-0.5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="w-65 rounded-lg p-2">
              <div className="relative h-24 overflow-hidden rounded-md border bg-gray-50">
                <div className="absolute top-2.5 left-5.5 scale-95 rounded-lg border border-gray-300 bg-gray-100 p-2 whitespace-nowrap shadow-lg/3">
                  <p className="mb-1.5 px-1 py-px">
                    Formatted like this document, draft a witness statement for John Doe.
                  </p>
                  <div className="flex w-fit items-center gap-1 rounded-md border bg-white px-1.5 py-0.75">
                    <Icon className="material-icon-theme--word text-xl text-blue-400" />
                    <div className="-space-y-0.5">
                      <p>example-witness-st...</p>
                      <p className="text-[11px] font-light text-gray-400">DOCX</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-1.25 pt-2.5 pb-0.5">
                <p className="mb-1 font-medium text-gray-900">Attach files in Kim</p>
                <p className="text-gray-600">
                  Try uploading a template to capture its formatting, or search for inconsistencies against a relevant{' '}
                  {t('witness statement')}.
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={KIM_FILE_INPUT_ACCEPT}
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length > 0) addFiles?.(files);
            e.target.value = '';
          }}
        />

        <div className="flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={'ghost'} size={'sm'} className="pr-0">
                Suggested Questions
                <Icon className="lucide--chevron-down" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="group/suggestions -mt-1.5 max-h-90 max-w-md space-y-1.5 bg-gray-100 p-1.5"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {suggestedQuestions.length === 0 ? (
                <DropdownMenuItem disabled className="text-xs">
                  No suggested questions
                </DropdownMenuItem>
              ) : (
                <>
                  <p className="px-1.5 py-px text-xs text-gray-400">
                    <strong>{suggestedQuestions.length}</strong> suggested question
                    {suggestedQuestions.length === 1 ? '' : 's'}
                  </p>
                  {suggestedQuestions.map((prompt, index) => (
                    <DropdownMenuItem
                      key={index}
                      className="bg-white px-2.25 py-1.5 whitespace-normal text-gray-700 hover:bg-white! group-has-[:hover]/suggestions:[&:not(:hover)]:text-gray-600 group-has-[:hover]/suggestions:[&:not(:hover)]:opacity-80"
                      onClick={() => {
                        setQuestion(prompt);
                        inputRef.current?.focus();
                      }}
                    >
                      {prompt}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <TemplatesPopover
            onSelect={handleTemplateSelect}
            onOpenTemplateBuilder={() => setIsTemplateBuilderOpen(true)}
            onEditTemplate={setEditingTemplate}
          />
        </div>
      </div>

      <KimInput
        ref={inputRef}
        className="max-h-[50vh] min-h-28 w-full p-4 py-3 pb-0"
        placeholder="What is this Matter about?"
        value={question}
        onChange={setQuestion}
        onSubmit={handleSubmit}
        disabled={isLoading}
        uploadedFiles={uploadedFiles}
        isUploading={isUploading}
        onFilesAdded={addFiles}
        onFileRemove={removeFile}
        onSaveAsTemplate={() => {
          setTemplateInitialDraft({ title: '', body: question });
          setIsTemplateBuilderOpen(true);
        }}
        autoFocus
      />

      <TemplateBuilderDialog
        open={isTemplateBuilderOpen || editingTemplate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setIsTemplateBuilderOpen(false);
            setEditingTemplate(null);
            setTemplateInitialDraft(undefined);
          } else {
            setIsTemplateBuilderOpen(true);
          }
        }}
        template={editingTemplate ?? undefined}
        initialDraft={templateInitialDraft}
      />

      <Link
        to={`/matters/${matterId}/subsets/${subsetId}/kim`}
        className="mt-5 flex items-center justify-center gap-1.5 text-center text-xs text-gray-400 duration-150 hover:text-gray-600"
        onClick={() => {
          trackCustomEvent(TrackingEventsV2.KimReturnToOldExperience, { matterId, subsetId });
        }}
      >
        <Icon className="lucide--history mb-0.5 -ml-1" /> View your old Kim threads
      </Link>
    </div>
  );
}
